<script lang="ts">
  import ModalDialog from "@kontextwerk/web-sdk/ui/overlay/ModalDialog.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { buildArchiveWorkOrderApprovalDescription } from "../../../../../../../../_shared/_logic/components-work-orders-action-tooltips"
  import { archiveCreatedWorkOrders } from "./_actions/archive-created-work-orders"
  import ProgressStepsContent from "@kontextwerk/web-sdk/ui/navigation/ProgressStepsContent.svelte"
  import ProgressSteps, {
    type ProgressStep,
    type ProgressStepContext,
  } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import { onDestroy, onMount, untrack } from "svelte"
  import type { Snippet } from "svelte"
  import {
    firstCurrentWorkflowStep,
    LOCKED_WORKFLOW_STEP_MESSAGE,
    selectedWorkflowStepIndex,
    workflowStateForStep,
  } from "../../../../../../../../_shared/_logic/workflow-progress"
  import BatchStepLoadList, { createLoadListState } from "./steps/a_load_list"
  import BatchStepPlanAppointments from "./steps/b_plan_appointments"
  import BatchStepContactOrders, { createContactOrderPlanningDraft } from "./steps/c_contact_orders"
  import BatchStepSummary from "./steps/d_summary"
  import type {
    EmployeeOption,
    WorkingHoursByUser,
  } from "../../../../../../../../../../_shared/_logic/form-state"
  import type {
    BatchUploadCancellationAction,
    BatchUploadInit,
    BatchUploadOutputs,
    BatchUploadReferenceData,
    BatchUploadStepId,
    BatchUploadWorkflowEvent,
  } from "./_shared/_logic/batch-upload-contract"
  import {
    buildBatchUploadReferenceData,
    loadBatchUploadReferenceData,
  } from "./_logic/referenceData"
  import {
    applyBatchUploadEvent,
    canEnterBatchUploadStep,
    completedBatchUploadStepIndex,
    hasUsableCreateWorkOrdersOutput,
    invalidStepMessage,
  } from "./_logic/transitions"
  import { validateBatchUploadPropertyLimit } from "./_shared/_logic/batch-upload-limits"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setCloseDisabled?: (disabled: boolean) => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    organizationId: string
    trialRestrictionsActive?: boolean | null
    properties?: Property[]
    customers?: Customer[]
    contactPoints?: ContactPoint[]
    orderTypes?: OrderType[]
    defaultOrderNumber?: string | null
    employees?: EmployeeOption[]
    workingHoursByUser?: WorkingHoursByUser
    onCompleted?: (created: WorkOrder[]) => void | Promise<void>
  }

  let {
    organizationId,
    trialRestrictionsActive = false,
    properties = [],
    customers = [],
    contactPoints = [],
    orderTypes = [],
    defaultOrderNumber = null,
    employees = [],
    workingHoursByUser = {},
    onCompleted = async () => {},
    onremoveOverlay = () => {},
    onshowOverlay = null,
    setActionButtonSnippet = null,
    setCloseDisabled = () => {},
  }: Props & OverlayHostProps = $props()

  let parentRefreshPromise: Promise<void> | null = null
  let ownerClosed = false
  let mutatedSinceOpen = false
  let completedInvalidation = false
  let completionError = $state<string | null>(null)
  const originalOnCompleted = untrack(() => onCompleted)
  const setOwnerCloseDisabled = untrack(() => setCloseDisabled)
  type CancellationPhase =
    | "idle"
    | "confirming"
    | "archiving"
    | "incomplete"
    | "refreshing"
    | "refresh_failed"
    | "closed"
  let cancellationApproval: ((approved: boolean) => void) | null = null
  let cancellationDescription = $state("")
  let cancellationPhase = $state<CancellationPhase>("idle")
  let unresolvedArchiveIds = $state<string[]>([])
  let cancellationError = $state<string | null>(null)
  const cancellationAction = $derived<BatchUploadCancellationAction>({
    label:
      cancellationPhase === "refresh_failed"
        ? "Liste aktualisieren und schließen"
        : cancellationPhase === "incomplete"
          ? "Archivierung wiederholen"
          : "Aufträge stornieren und abbrechen",
    pending: ["confirming", "archiving", "refreshing", "closed"].includes(cancellationPhase),
    blocksPlanning: cancellationPhase !== "idle",
    reason:
      cancellationPhase === "idle"
        ? null
        : cancellationPhase === "confirming"
          ? "Bitte den Abbruch bestätigen oder zur Planung zurückkehren."
          : "Die Sammelplanung wird abgebrochen. Die Aufträge können nicht weitergeplant werden.",
  })

  const handleCompleted = async (created: WorkOrder[]) => {
    await originalOnCompleted(created)
    completedInvalidation = true
  }

  const markWorkflowMutated: BatchUploadInit["onWorkflowMutated"] = async (created) => {
    mutatedSinceOpen = true
    completedInvalidation = false

    // A committed request can outlive its overlay. Refresh the original parent,
    // but never navigate a disposed workflow or reopen its editor.
    if (ownerClosed || organizationId !== init.organizationId) await handleCompleted(created)
  }

  const refreshParentAfterClose = async (created: WorkOrder[]) => {
    if (parentRefreshPromise) return parentRefreshPromise

    if (completedInvalidation) return

    parentRefreshPromise = (async () => {
      try {
        completionError = null
        await handleCompleted(created)
      } catch (err) {
        console.error("batch_upload_parent_refresh_failed", err)
        completionError =
          "Die Sammelplanung ist abgeschlossen, aber die Auftragsliste konnte nicht automatisch aktualisiert werden."
        throw err
      }
    })()
    try {
      await parentRefreshPromise
    } finally {
      parentRefreshPromise = null
    }
  }

  const finishAndClose = async (created: WorkOrder[]) => {
    await refreshParentAfterClose(created)

    if (!ownerClosed && organizationId === init.organizationId) onremoveOverlay?.()
  }

  const init: BatchUploadInit = untrack(() => ({
    organizationId,
    trialRestrictionsActive,
    properties,
    customers,
    contactPoints,
    orderTypes,
    defaultOrderNumber,
    employees,
    workingHoursByUser,
    onWorkflowMutated: markWorkflowMutated,
  }))
  const loadListState = createLoadListState({
    organizationId: init.organizationId,
    properties: init.properties,
  })
  const contactPlanningDraft = createContactOrderPlanningDraft(init.organizationId)

  let outputs = $state<BatchUploadOutputs>({})
  let selectedStep = $state<BatchUploadStepId>("load_list")
  let referenceData = $state<BatchUploadReferenceData>(buildBatchUploadReferenceData(init, null))
  let referenceDataReady = $state(false)
  const completedStepIndex = $derived(completedBatchUploadStepIndex(outputs))
  const planningEntryError = $derived(
    validateBatchUploadPropertyLimit(
      outputs.loadList?.selectedRows.map((row) => row.propertyId) ?? [],
    ),
  )
  const stepOrder = ["load_list", "plan_appointments", "contact_orders", "results"] as const

  const cancellationContextActive = () => !ownerClosed && organizationId === init.organizationId

  const refreshAndCloseCancelledPlanning = async () => {
    cancellationPhase = "refreshing"
    try {
      await refreshParentAfterClose([])

      if (!cancellationContextActive()) return

      cancellationPhase = "closed"
      onremoveOverlay?.()
    } catch {
      if (!cancellationContextActive()) return

      completionError = null
      cancellationPhase = "refresh_failed"
      cancellationError =
        "Die Aufträge sind archiviert, aber die Auftragsliste konnte nicht aktualisiert werden. Bitte die Aktualisierung erneut versuchen."
    }
  }

  const cancelPlanning = async () => {
    if (
      !cancellationContextActive() ||
      cancellationAction.pending ||
      outputs.planAppointments ||
      !hasUsableCreateWorkOrdersOutput(outputs.loadList)
    )
      return

    // Snapshot the complete creation result, never the filtered or edited preview.
    const createdIds = [...new Set(outputs.loadList!.createdRows.map((row) => row.workOrder.id))]
    setOwnerCloseDisabled(true)
    try {
      if (cancellationPhase === "idle") {
        cancellationPhase = "confirming"
        cancellationDescription =
          buildArchiveWorkOrderApprovalDescription(createdIds.length) +
          " Betroffen sind alle in diesem Durchgang angelegten Aufträge. Anschließend wird die Sammelplanung geschlossen."
        const approved = await new Promise<boolean>((resolve) => {
          cancellationApproval = resolve
        })
        cancellationApproval = null

        if (!cancellationContextActive()) return

        if (!approved) {
          cancellationPhase = "idle"

          return
        }

        unresolvedArchiveIds = createdIds
      }

      cancellationError = null
      completionError = null

      if (cancellationPhase === "refresh_failed") {
        await refreshAndCloseCancelledPlanning()

        return
      }

      cancellationPhase = "archiving"
      const result = await archiveCreatedWorkOrders(unresolvedArchiveIds)
      // Even a lost response may have committed. Refresh the original owner after disposal.
      await markWorkflowMutated([])

      if (!cancellationContextActive()) return

      unresolvedArchiveIds = result.unresolvedIds

      if (unresolvedArchiveIds.length) {
        cancellationPhase = "incomplete"
        const reasons = [...new Set(result.issues.map((issue) => issue.message))]
        cancellationError = `${unresolvedArchiveIds.length} von ${createdIds.length} Aufträgen sind noch nicht als archiviert bestätigt. ${reasons.join(" ")}`

        return
      }

      await refreshAndCloseCancelledPlanning()
    } catch {
      if (!cancellationContextActive()) return

      cancellationPhase = cancellationPhase === "confirming" ? "idle" : "incomplete"
      cancellationError = "Der Abbruch konnte nicht abgeschlossen werden. Bitte erneut versuchen."
    } finally {
      setOwnerCloseDisabled(false)
    }
  }

  const handleWorkflowEvent = async (event: BatchUploadWorkflowEvent) => {
    if (event.type === "cancel_requested") {
      await cancelPlanning()

      return
    }

    if (ownerClosed || cancellationAction.blocksPlanning) return

    if (event.type === "planning_preview_invalid") {
      completionError =
        "Die Terminvorschau konnte nicht vollständig aufgebaut werden. Die Aufträge sind bereits gespeichert; bitte Vorschau erneut laden."

      return
    }

    if (event.type === "finish") {
      const createdOrders =
        event.createdOrders ?? outputs.loadList?.createdRows.map((row) => row.workOrder) ?? []
      await finishAndClose(createdOrders)

      return
    }

    completionError = null
    outputs = applyBatchUploadEvent(outputs, event)
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(null)
    let alive = true
    void loadBatchUploadReferenceData(init).then((next) => {
      if (alive) {
        referenceData = next
        referenceDataReady = true
      }
    })

    return () => {
      alive = false
    }
  })
  onDestroy(() => {
    ownerClosed = true
    cancellationApproval?.(false)
    cancellationApproval = null

    setActionButtonSnippet?.(null)
    setOwnerCloseDisabled(false)

    if (
      mutatedSinceOpen &&
      !completedInvalidation &&
      !parentRefreshPromise &&
      cancellationPhase !== "archiving"
    ) {
      void Promise.resolve(originalOnCompleted([])).catch((err) => {
        console.error("batch_upload_close_invalidation_failed", err)
      })
    }
  })

  const completedSteps = $derived.by(() => {
    const completed: BatchUploadStepId[] = []

    if (hasUsableCreateWorkOrdersOutput(outputs.loadList)) completed.push("load_list")

    if (outputs.planAppointments) completed.push("plan_appointments")

    if (outputs.contactOrders) completed.push("contact_orders")

    return completed
  })
  const skippedSteps = $derived.by(() => {
    if (outputs.planAppointments && !outputs.planAppointments.createdEvents.length) {
      return ["contact_orders"] satisfies BatchUploadStepId[]
    }

    return [] satisfies BatchUploadStepId[]
  })
  const currentStep = $derived(
    firstCurrentWorkflowStep<BatchUploadStepId>({
      stepOrder,
      completedSteps,
      skippedSteps,
      selectedStep,
    }),
  )
  const selectedIndex = $derived(
    selectedWorkflowStepIndex<BatchUploadStepId>({
      stepOrder,
      completedSteps,
      skippedSteps,
      selectedStep,
    }),
  )
  const handleSelectedIndexChange = (index: number) => {
    if (cancellationAction.blocksPlanning) return

    const next = stepOrder[index]

    if (next === "plan_appointments" && !canEnterBatchUploadStep(outputs, next)) {
      completionError = invalidStepMessage(outputs, next)

      return
    }

    completionError = null

    if (next) selectedStep = next
  }

  const progressStatus = (index: number) => (completedStepIndex >= index ? "done" : undefined)
  const steps: ProgressStep[] = $derived([
    {
      id: "load_list",
      label: "Aufträge vorbereiten",
      description: "",
      hideDefaultAction: true,
      progressStatus: progressStatus(0),
      workflowState: workflowStateForStep({
        step: "load_list",
        currentStep,
        completedSteps,
        skippedSteps,
      }),
      snippet: loadListStep,
    },
    {
      id: "plan_appointments",
      label: "Termine planen",
      description: "",
      hideDefaultAction: true,
      progressStatus: progressStatus(1),
      workflowState: !hasUsableCreateWorkOrdersOutput(outputs.loadList)
        ? "locked"
        : workflowStateForStep({
            step: "plan_appointments",
            currentStep,
            completedSteps,
            skippedSteps,
          }),
      snippet: planAppointmentsStep,
    },
    {
      id: "contact_orders",
      label: "Kunden informieren",
      description: "",
      hideDefaultAction: true,
      progressStatus: progressStatus(2),
      workflowState: workflowStateForStep({
        step: "contact_orders",
        currentStep,
        completedSteps,
        skippedSteps,
      }),
      snippet: contactOrdersStep,
    },
    {
      id: "results",
      label: "Ergebnisübersicht",
      description: "",
      hideDefaultAction: true,
      workflowState: workflowStateForStep({
        step: "results",
        currentStep,
        completedSteps,
        skippedSteps,
      }),
      snippet: summaryStep,
    },
  ])
</script>

{#if cancellationPhase === "confirming"}
  <ModalDialog
    appearance="plain"
    title="Sammelplanung abbrechen?"
    closeLabel="Zurück zur Planung"
    onClose={() => cancellationApproval?.(false)}
  >
    {#snippet content()}
      <p class="cancellation-description">{cancellationDescription}</p>
    {/snippet}
    {#snippet footerSnippet()}
      <CTA
        type="primaryButton"
        darkMode={false}
        content="Aufträge archivieren und schließen"
        onClick={() => cancellationApproval?.(true)}
      />
    {/snippet}
  </ModalDialog>
{/if}

{#snippet loadListStep(ctx)}
  {#if referenceDataReady}
  <BatchStepLoadList
      {ctx}
      {init}
      {loadListState}
      {referenceData}
      output={outputs.loadList}
      locked={ctx.readOnly || Boolean(outputs.loadList)}
      readOnly={ctx.readOnly}
      onEvent={handleWorkflowEvent}
    />
  {:else}<p>Auftragsdaten werden geladen…</p>{/if}
{/snippet}

{#snippet planAppointmentsStep(ctx)}
  {#if outputs.loadList && hasUsableCreateWorkOrdersOutput(outputs.loadList)}
    <BatchStepPlanAppointments
      {ctx}
      {init}
      {referenceData}
      loadList={outputs.loadList}
      output={outputs.planAppointments ?? outputs.planningProgress}
      {cancellationAction}
      onEvent={handleWorkflowEvent}
    />
  {/if}
{/snippet}

{#snippet contactOrdersStep(ctx)}
  {#if outputs.loadList && outputs.planAppointments}
    <BatchStepContactOrders
      planningDraft={contactPlanningDraft}
      {ctx}
      {init}
      {referenceData}
      loadList={outputs.loadList}
      planAppointments={outputs.planAppointments}
      output={outputs.contactOrders}
      onEvent={handleWorkflowEvent}
    />
  {/if}
{/snippet}

{#snippet summaryStep(ctx)}
  {#if outputs.loadList && outputs.planAppointments}
    <BatchStepSummary
      {ctx}
      {referenceData}
      loadList={outputs.loadList}
      planAppointments={outputs.planAppointments}
      contactOrders={outputs.contactOrders}
      onEvent={handleWorkflowEvent}
    />
  {/if}
{/snippet}

<div class="batch-overlay form light">
  {#if completionError || cancellationError}
    <ProgressStepsContent>
      <div class="completion-error" role="alert">
        {cancellationError ?? completionError}
      </div>
    </ProgressStepsContent>
  {/if}
  <ProgressSteps
    {steps}
    showControls={false}
    darkMode={true}
    syncUrl={false}
    statusMode="explicit"
    navigationMode="guarded"
    lockedStepMessage={planningEntryError ?? LOCKED_WORKFLOW_STEP_MESSAGE}
    {selectedIndex}
    onSelectedIndexChange={handleSelectedIndexChange}
  />
</div>

<style lang="less">
  .cancellation-description {
    margin: 0;
    color: var(--text-invers-100);
    line-height: 1.5;
  }

  .batch-overlay {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    height: fit-content;
    padding-bottom: 1rem;
  }

  .completion-error {
    border: 1px solid var(--error-100, #f05);
    color: var(--error-100, #f05);
    padding: 0.75rem 0.9rem;
    font-weight: 700;
  }
</style>
